import {getRequestsList} from './requests.useCase'
import {requestsUtils} from '../utils'
import { 
    CampaignsService, 
    UpdatesService,
    RequestsService
} from '../services'
import moment from 'moment'

export const getUpdatesList = (start, perPage, must, must_not) => {
    return UpdatesService.getUpdates({start, size: perPage, must, must_not}).then(async response => {

        let totalCount = response.data.total.value;
        let updates = response.data.hits.map(
            hit => ({
                    ...hit._source,
                    id: hit._id
                })
        ) || []

        if(!updates.length) return {updates, totalCount}

        return {
            updates : updates.map(item => ({
                campaign: {
                    title: item.cname,
                    image: item.cimage,
                },
                ...item,
                partnerUpdates: null,
                expandLoading: false
            })),
            totalCount
        }
        
    }).catch(e => console.log(e))
}


const checkAndFixUpdateDateIssues = async (updateData, partnerCampaigns) => {
    
    const spotsWithPositions = updateData.spots.map((item, index) => {
        if(item && item.cpid) {
            return {
                ...item,
                index
            }
        }
    }).filter(item => item && item.cpid)

    // query searching for accurate requests of the selected update using other params than update_date_id
    const query = requestsUtils.getAcceptedRequestsQueryByMultipleParams(updateData.cid, partnerCampaigns, updateData.date)

    const result = await getRequestsList(0, 100, query)

    const requests = result.requests.filter(item => {
        const [partner, owner] = item.receiver.campaign === updateData.cid ? [item.sender, item.receiver] : [item.receiver, item.sender]
        return spotsWithPositions.find(x => x.cpid === partner.campaign && x.index === owner.spot_position)
    })

    // update corresponding requests filling accurate update_date_ids
    requests.forEach(req => {
        let side = req.receiver.campaign === updateData.cid ? 'receiver' : 'sender'
        RequestsService.updateRequestData(req.id, {
            [side+'.update_date_id']: updateData.id
        })
    });

    return requests
}


const getAndFixMissingUpdates = async (allPartnerUpdates, requestsOfUpdates) => {

    let missingUpdates = requestsOfUpdates.filter(item => {
        return allPartnerUpdates.map(x => x.id).indexOf(item.campaign.update_date_id) === -1
    })

    let res = await Promise.all(missingUpdates.map(el => {

        const campaign = el.campaign.campaign === el.sender.campaign ? el.receiver.campaign : el.sender.campaign
        return getUpdatesList(0, 1, [
            {
                term: {
                    'cid' : el.campaign.campaign
                },
                
            },
            {
                term: { 
                    "spots.cpid": campaign 
                },
            },
            {
                range: {
                    'date': {
                        gte: moment(el.campaign.update_date._seconds*1000).subtract(1, 'days').format('YYYY-MM-DD'),
                        lte: moment(el.campaign.update_date._seconds*1000).add(1, 'days').format('YYYY-MM-DD'),
                    }
                }
            }
        ])

    }));

    res = res.map(item => item.updates[0])

    missingUpdates.forEach(item => {
        let updateData = res.find(x => x.cid === item.campaign.campaign)
        const side = item.campaign.campaign === item.sender.campaign ? 'sender' : 'receiver'
        RequestsService.updateRequestData(item.requestId, {
            [side+'.update_date_id']: updateData.id
        })
    })

    return [...allPartnerUpdates, ...res]
}

export const getPartnerUpdates = async updateData => {

    const partnerCampaigns = updateData.spots.filter(item => item && item.cpid).map(item => item.cpid)

    // query searching for requests of the selected update using update_date_id
    const query = requestsUtils.getAcceptedRequestsQueryByUpdateDateId(updateData.id)

    const res = await getRequestsList(0, 5, query)

    // check for the consequences of wrong update_date_ids 
    const requests = res.requests.length !== partnerCampaigns.length ? 
                    await checkAndFixUpdateDateIssues(updateData, partnerCampaigns) :
                    res.requests

    // getting requests for showing updates' data mixed with requests data
    const requestsOfUpdates =  requests.map(item => ({
        campaign: {
            ...(item.receiver.campaign === updateData.cid ? item.sender : item.receiver),
        },
        sender: item.sender,
        receiver: item.receiver,
        requestId: item.id
    }))

    const response = await getUpdatesList(0, requestsOfUpdates.length, [
        {
            terms: {
                '_id': 
                    [...requestsOfUpdates.map(item => item.campaign.update_date_id)]
                }
            }
        ]
    )
        
    // check for the consequences of wrong update_date_ids 
    const allPartnerUpdates = response.updates.length !== requestsOfUpdates.length ? 
                            await getAndFixMissingUpdates(response.updates, requestsOfUpdates) :
                            [...response.updates]


    return allPartnerUpdates.map(
        item => {
            // setting status data based on the data above 
            const update = {
                ...item,
                status: 'pending',
            }

            if(update.spots.find(
                (spot) => spot && spot.posted && spot.cpid && spot.cpid === updateData.cid
            )) {
                update.status = 'posted'
            }

            // return merged updates and requests data
            return {
                status: update.status,
                id: update.id,
                ...requestsOfUpdates.find(itmInner => update.id === itmInner.campaign.update_date_id || update.cid === itmInner.campaign.campaign)
            }
        }
    ) 
}



export const getUnscheduledUpdates = async (authorId) => {

    // get all campaigns of a user
    const snapshot = await CampaignsService.getCampaignsByAuthorId(authorId).get()

    const campaigns = snapshot.docs.map(
        doc => ({
            ...doc.data(),
            id: doc.id,
        })
    )


    // get updates by camapigns ids', also get the latest update of each campaign via aggregations
    const updates = await UpdatesService.getUpdates({
        start: 0, 
        size: 1000, 
        must: [{
            "bool": {
                "must": {
                    "terms": {
                        "cid": campaigns.map(item => item.id)
                    }
                }
            }
        }],
        aggs: {
            "by_cid": {
                "terms": {
                    "field": 'cid',
                    "size": 1000
                },
                "aggs": {
                    "max_date": {
                        "top_hits": {
                            "size": 1,
                            "sort": {
                                "date": "desc"
                            }
                        }
                    }
                }
            }
        }
    })


    // get campaigns ids' with updates
    const campaignsIdsWithUpdates = [...new Set(updates.data.hits.hits.map(
        hit => hit._source.cid
    ))]


    // filter campaigns ids' not having upcoming updates 
    let campaignsIdsWithNoUpcomingUpdates = updates.data.aggregations.by_cid.buckets.filter(item => {
        const updateDate = item.max_date.hits.hits[0]._source.date
        return moment().isAfter(moment(updateDate).add(1, 'days'))
    }).map(x => x.max_date.hits.hits[0]._source.cid)


    // filter campaigns not having updates
    const campaignsWithNoUpdates = campaigns.filter(item => {
        return campaignsIdsWithUpdates.indexOf(item.id) === -1
    })

    // filter campaigns not having upcoming updates 
    const campaignsWithNoUpcomingUpdates = campaigns.filter(item => {
        return campaignsIdsWithNoUpcomingUpdates.includes(item.id)
    })

    // return all unscheduled campaigns
    return [...campaignsWithNoUpcomingUpdates, ...campaignsWithNoUpdates].map(item => {
        return {
            ...item,
            campaign: {
                id: item.id,
                image: item.thumbnails && item.thumbnails.small || item.image,
                title: item.title
            }
    }})
}